import { useState } from 'react';

import { useLazyGetLatestAccuracyJobsQuery } from '../../generate-accuracy/api/accuracyApiSlice';
import { AccuracyJobSummaryRow } from '../../generate-accuracy/types/response';
import { buildAccuracyDashboardUrl } from '../emitters/buildAccuracyDashboardUrl';

export interface UseViewAccuracyDashboardResult {
  handleViewAccuracyDashboard: (projectId: string) => void;
  dashboardUnavailable: boolean;
  clearDashboardUnavailable: () => void;
}

/**
 * Resolves the row's optimizationProjectId, setName and accuracyIterationId
 * from the latest-jobs list (the annotation-tool projectId and the
 * dashboard's optimizationProjectId are different values — see the design
 * doc's "Project id mapping" section) and opens the accuracy dashboard
 * deep-linked to that project, batch and iteration.
 *
 * The tab is opened synchronously, before the lookup resolves, so browsers
 * don't treat it as a blocked popup; its location is then filled in once the
 * lookup settles. If the URL can't be built (dashboard URL not configured, or
 * the lookup failed), the blank tab is closed and `dashboardUnavailable` is
 * set so the caller can surface the misconfiguration instead of leaving a
 * dead tab open.
 */
// TODO: cleanup with cp_trigger_accuracy_from_ui ff
export const useViewAccuracyDashboard = (): UseViewAccuracyDashboardResult => {
  const [fetchLatestAccuracyJobs] = useLazyGetLatestAccuracyJobsQuery();
  const [dashboardUnavailable, setDashboardUnavailable] = useState(false);

  const handleViewAccuracyDashboard = (projectId: string) => {
    const newTab = window.open('', '_blank');

    fetchLatestAccuracyJobs()
      .unwrap()
      .then((result) => {
        const job: AccuracyJobSummaryRow | undefined = result.data.find(
          (row) => row.projectId === projectId
        );
        return buildAccuracyDashboardUrl({
          optimizationProjectId: job?.optimizationProjectId,
          setName: job?.setName,
          accuracyIterationId: job?.accuracyIterationId,
        });
      })
      .catch(() => buildAccuracyDashboardUrl({}))
      .then((url) => {
        if (url) {
          if (newTab) {
            newTab.location.href = url;
          }
        } else {
          newTab?.close();
          setDashboardUnavailable(true);
        }
      });
  };

  return {
    handleViewAccuracyDashboard,
    dashboardUnavailable,
    clearDashboardUnavailable: () => setDashboardUnavailable(false),
  };
};
